import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import {
  api,
  ApiError,
  fetchSkuLeadTimes,
  type PlanRow,
  type PurchasePlanResult,
  type Sku,
  type SkuDetail,
  type SkuFamily,
  type SkuProductTaxonomy,
} from '../api/client.js';
import {
  listingIssueText,
  retiredNote,
  taxonomyFacts,
  taxonomyLinks,
} from '../lib/sku-taxonomy.js';
import { productPagePathFromKey } from '../lib/products.js';
import { parseCount, parseMoney, type ParsedField } from '../lib/parse-input.js';
import { useSession } from '../auth/useSession.js';
import {
  Alert,
  Button,
  Card,
  Input,
  PageHeader,
  Spinner,
  StockNote,
  Textarea,
} from '../components/ui.js';
import {
  AdjacentShipmentsNote,
  Item,
  LEDGER_EVENTS_SHOWN,
  LeadTimeLinesTable,
  LedgerCard,
  MonthlyHistoryCard,
  PlanRowFacts,
  SellThroughCard,
  StockoutsCard,
  hasAdjacentShipments,
} from '../components/DetailSections.js';
import { day, dateTime, integer, money } from '../format.js';
import { KEY_SOURCE_LABEL, familyPath } from '../lib/families.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { OffersCard } from '../components/OffersCard.js';
import { ProductThumb } from '../components/ProductThumb.js';

import { WhenVisible } from '../components/WhenVisible.js';

const EmbeddedSalesStock = lazy(() => import('../charts/EmbeddedSalesStock.js'));

/**
 * One SKU: what the listing says, the owner's notes, two years month by
 * month, and the latest ledger events. Shipments and transfers are shown
 * with the sign Amazon gives them, negative for units leaving.
 */

export function SkuDetailPage() {
  const { id } = useParams();
  const session = useSession();
  const canEdit = session.status === 'signedIn' && can(session.user.role, 'data:import');

  const detail = useQuery({
    queryKey: ['sku', id],
    queryFn: () => api.get<SkuDetail>(`/api/skus/${id}`),
    enabled: id !== undefined,
  });

  if (detail.isPending) return <Spinner label="Loading SKU" />;
  if (detail.isError) return <Alert>{detail.error.message}</Alert>;

  const { sku, history, sellThrough, family, taxonomy, ledger, currentPlan, delistedOn } =
    detail.data;
  const monthMeta = new Map(sellThrough.monthly.map((m) => [m.month, m]));

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted">
        <Link to="/skus" className="hover:underline">
          SKUs
        </Link>{' '}
        / {sku.sellerSku}
      </div>
      <div className="flex items-start gap-4">
        <ProductThumb asin={sku.asin} title={sku.title} size={120} />
        <PageHeader title={sku.title ?? sku.sellerSku} />
      </div>

      <AnalysisBanner />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card className="p-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <Item label="SKU" value={sku.sellerSku} />
            <Item label="ASIN" value={sku.asin ?? '—'} />
            <Item label="FNSKU" value={sku.fnsku ?? '—'} />
            <Item
              label="Status"
              value={
                // A listing deleted in Seller Central keeps Amazon's last status on
                // the row; say it is gone, with that status beside it.
                delistedOn
                  ? `No longer listed since ${day(delistedOn)} (Amazon status: ${sku.status ?? 'none'})`
                  : (sku.status ?? '—')
              }
            />
            <Item label="On hand" value={integer(history.onHand)} />
            <Item label="Price" value={money(sku.price)} />
            <Item label="First sale" value={day(history.firstSale)} />
            <Item label="Last sale" value={day(history.lastSale)} />
            <Item label="Last receipt" value={day(history.lastReceiptDate)} />
            <Item label="Listed since" value={sku.openDate ? dateTime(sku.openDate) : '—'} />
            <Item label="Last seen in a listing" value={dateTime(sku.lastSeenAt)} />
            <Item label="In stock" value={history.inStockNow ? 'Yes' : 'No'} />
          </dl>
          <div className="mt-3 border-t border-line pt-2 text-xs">
            <StockNote source={history.stockSource} asOf={history.stockAsOf} />
          </div>
        </Card>
        <NotesCard sku={sku} canEdit={canEdit} />
      </div>

      <ProductCard taxonomy={taxonomy} />

      <FamilyCard sku={sku} family={family} canEdit={canEdit} />

      <PlanCard skuId={sku.id} />

      {canEdit && <PlanningForm sku={sku} currentPlan={currentPlan} />}

      <LeadTimesCard sellerSku={sku.sellerSku} />

      {sku.asin && <OffersCard asin={sku.asin} />}

      <SellThroughCard detail={sellThrough} />

      <Card className="p-4">
        <WhenVisible>
          <Suspense fallback={<Spinner label="Loading chart" />}>
            <EmbeddedSalesStock scope="sku" chartKey={String(sku.id)} />
          </Suspense>
        </WhenVisible>
      </Card>

      <MonthlyHistoryCard months={history.months} monthMeta={monthMeta} />

      <StockoutsCard detail={sellThrough} />

      <LedgerCard ledger={ledger} shown={LEDGER_EVENTS_SHOWN} />
    </div>
  );
}

/**
 * The SKU's product as the taxonomy resolved it (category, series, model, color,
 * size, or a string's gauge and put-up, and where that came from), with a link to
 * the product in the Products browser and, while it still needs a decision, to
 * the review queue. Read-only here; decisions are made on the review page.
 */
function ProductCard({ taxonomy }: { taxonomy: SkuProductTaxonomy }) {
  const links = taxonomyLinks(taxonomy);
  const issue = listingIssueText(taxonomy);
  const retired = retiredNote(taxonomy);
  // The product page: every SKU on this SKU's ASIN in one place (its lineage).
  const productPage = productPagePathFromKey(taxonomy.productKey);
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-sm font-medium text-body">Product</h2>
        {productPage && (
          <Link to={productPage} className="text-sm text-brand-600 hover:underline">
            Open product
          </Link>
        )}
        <Link to={links.products} className="text-sm text-brand-600 hover:underline">
          Open in Products
        </Link>
        {links.review && (
          <Link to={links.review} className="text-sm text-brand-600 hover:underline">
            Needs review
          </Link>
        )}
      </div>
      <dl
        aria-label="Product taxonomy"
        className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3"
      >
        {taxonomyFacts(taxonomy).map((fact) => (
          <Item key={fact.label} label={fact.label} value={fact.value} />
        ))}
      </dl>
      {issue && (
        <div className="mt-3">
          <Alert tone="warning">{issue}</Alert>
        </div>
      )}
      {retired && (
        <p className="mt-2 text-xs text-muted" data-testid="retired-note">
          {retired}
        </p>
      )}
    </Card>
  );
}

/**
 * The SKU's product family: which family key it falls under and why, a link to
 * the family page, and an override the owner can set to move the SKU into a
 * different family (blank clears it, falling back to the parent ASIN).
 */
function FamilyCard({ sku, family, canEdit }: { sku: Sku; family: SkuFamily; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [override, setOverride] = useState(sku.familyOverride ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setOverride(sku.familyOverride ?? '');
  }, [sku.id, sku.familyOverride]);

  const save = useMutation({
    mutationFn: (value: string) =>
      api.patch<{ sku: Sku }>(`/api/skus/${sku.id}`, { familyOverride: value || null }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sku', String(sku.id)] }),
        queryClient.invalidateQueries({ queryKey: ['families'] }),
        queryClient.invalidateQueries({ queryKey: ['family'] }),
        queryClient.invalidateQueries({ queryKey: ['restock'] }),
      ]);
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(caught instanceof ApiError ? caught.message : 'Could not save the family override.');
    },
  });

  const dirty = override.trim() !== (sku.familyOverride ?? '');

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Family</h2>
      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted">Name</dt>
          <dd>
            <Link to={familyPath(family.key)} className="text-brand-600 hover:underline">
              {family.name || family.key}
            </Link>
          </dd>
        </div>
        <Item label="Key" value={family.key} />
        <Item label="Key source" value={KEY_SOURCE_LABEL[family.keySource]} />
      </dl>
      {canEdit ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(override.trim());
          }}
          className="mt-3 flex flex-wrap items-end gap-3 border-t border-line pt-3"
        >
          <div className="min-w-56 flex-1">
            <label className="text-xs text-muted" htmlFor="family-override">
              Family override
            </label>
            <Input
              id="family-override"
              value={override}
              onChange={(e) => {
                setOverride(e.target.value);
                setSaved(false);
              }}
              placeholder="Parent ASIN or a name to group by; blank to clear"
              aria-label="Family override"
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-muted">
            {saved && !dirty && <span>Saved.</span>}
            <Button type="submit" variant="secondary" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save override'}
            </Button>
          </div>
          {error && (
            <div className="w-full">
              <Alert>{error}</Alert>
            </div>
          )}
        </form>
      ) : (
        <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
          {sku.familyOverride
            ? `Grouping overridden to "${sku.familyOverride}".`
            : 'Grouped from the parent ASIN, ASIN, or SKU. An importer can override this.'}
        </p>
      )}
    </Card>
  );
}

/**
 * The owner's planning inputs for one SKU: do-not-reorder and its note, case
 * pack, minimum order, lead-time override, unit cost, and a quantity override
 * with a note. Everything the agent CSV and the plan need, in one place. Each
 * numeric field is parsed strictly; nothing is saved on a parse error.
 */
type CurrentPlan = SkuDetail['currentPlan'];

function initialPlanningFields(s: Sku, currentPlan: CurrentPlan) {
  return {
    reorderNote: s.reorderNote ?? '',
    casePack: s.casePack === null ? '' : String(s.casePack),
    minOrderQty: s.minOrderQty === null ? '' : String(s.minOrderQty),
    leadTimeDaysOverride: s.leadTimeDaysOverride === null ? '' : String(s.leadTimeDaysOverride),
    unitCost: s.unitCost ?? '',
    // The quantity override and its note come from the current plan, not the SKU.
    overrideQty: currentPlan?.overrideQty == null ? '' : String(currentPlan.overrideQty),
    overrideNote: currentPlan?.overrideNote ?? '',
  };
}
type PlanningFields = ReturnType<typeof initialPlanningFields>;

function PlanningForm({ sku, currentPlan }: { sku: Sku; currentPlan: CurrentPlan }) {
  const queryClient = useQueryClient();
  const initial = initialPlanningFields(sku, currentPlan);
  const [fields, setFields] = useState<PlanningFields>(initial);
  const [excluded, setExcluded] = useState(sku.reorderExcluded);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Fields the user has edited but not yet saved; a refetch must not clobber them.
  const touched = useRef<Set<string>>(new Set());
  // The override is edited in the current plan; sent plans are read-only.
  const planId = currentPlan?.id ?? null;
  const overrideLocked = currentPlan === null || currentPlan.status === 'sent';

  // The observed lead time for this SKU, to suggest a value for the override. Same
  // query key as the Lead times card below, so React Query fetches it once.
  const leadTimes = useQuery({
    queryKey: ['sku-lead-times', sku.sellerSku],
    queryFn: () => fetchSkuLeadTimes(sku.sellerSku),
  });
  const suggested = leadTimes.data?.stats.suggestedLeadTimeDays ?? null;
  const observedN = leadTimes.data?.stats.n ?? 0;

  // When the SKU refetches (e.g. after an inline edit elsewhere), resync only the
  // fields the user has NOT touched, so an open tab never loses in-progress edits.
  useEffect(() => {
    const next = initialPlanningFields(sku, currentPlan);
    setFields((current) => {
      const merged = { ...current };
      for (const key of Object.keys(next) as (keyof PlanningFields)[]) {
        if (!touched.current.has(key)) merged[key] = next[key];
      }
      return merged;
    });
    if (!touched.current.has('reorderExcluded')) setExcluded(sku.reorderExcluded);
  }, [sku, currentPlan]);

  function edit<K extends keyof PlanningFields>(key: K, value: string) {
    touched.current.add(key);
    setSaved(false);
    setFields((f) => ({ ...f, [key]: value }));
  }

  const save = useMutation({
    mutationFn: async ({
      skuBody,
      override,
    }: {
      skuBody: Record<string, unknown>;
      override: Record<string, unknown> | null;
    }) => {
      if (Object.keys(skuBody).length > 0)
        await api.patch<{ sku: Sku }>(`/api/skus/${sku.id}`, skuBody);
      if (override && planId !== null) {
        await api.put(`/api/plans/${planId}/overrides`, { skuId: sku.id, ...override });
      }
    },
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      touched.current.clear();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sku', String(sku.id)] }),
        queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
        queryClient.invalidateQueries({ queryKey: ['families'] }),
        queryClient.invalidateQueries({ queryKey: ['restock'] }),
      ]);
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(caught instanceof ApiError ? caught.message : 'Could not save the planning fields.');
    },
  });

  function submit() {
    // Send ONLY the fields the user changed, so a stale tab cannot overwrite an
    // inline edit made elsewhere. Numeric fields are parsed strictly. The
    // quantity override and its note go to the current plan, not the SKU.
    const skuBody: Record<string, unknown> = {};
    const parsers: [keyof PlanningFields, () => ParsedField<number | string>][] = [
      ['casePack', () => parseCount(fields.casePack, { min: 1 })],
      ['minOrderQty', () => parseCount(fields.minOrderQty, { min: 1 })],
      ['leadTimeDaysOverride', () => parseCount(fields.leadTimeDaysOverride, { min: 0 })],
      ['unitCost', () => parseMoney(fields.unitCost)],
    ];
    for (const [name, parse] of parsers) {
      if (fields[name] === initial[name]) continue; // unchanged
      const parsed = parse();
      if (parsed.kind === 'error') {
        setError(`${name}: ${parsed.message}`);
        return;
      }
      skuBody[name] = parsed.kind === 'clear' ? null : parsed.value;
    }
    if (fields.reorderNote !== initial.reorderNote) {
      skuBody.reorderNote = fields.reorderNote.trim() || null;
    }
    if (excluded !== sku.reorderExcluded) skuBody.reorderExcluded = excluded;

    const override: Record<string, unknown> = {};
    if (fields.overrideQty !== initial.overrideQty) {
      const parsed = parseCount(fields.overrideQty, { min: 0 });
      if (parsed.kind === 'error') {
        setError(`overrideQty: ${parsed.message}`);
        return;
      }
      override.qty = parsed.kind === 'clear' ? null : parsed.value;
    }
    if (fields.overrideNote !== initial.overrideNote) {
      override.note = fields.overrideNote.trim() || null;
    }
    if (Object.keys(override).length > 0 && overrideLocked) {
      setError(
        currentPlan === null
          ? 'No plan is selected to hold the override.'
          : 'This plan is sent and cannot be edited. Duplicate it to change the override.',
      );
      return;
    }

    if (Object.keys(skuBody).length === 0 && Object.keys(override).length === 0) {
      setError('No changes to save.');
      return;
    }
    setError(null);
    save.mutate({ skuBody, override: Object.keys(override).length > 0 ? override : null });
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Planning</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="mt-3 space-y-4"
      >
        <label className="flex items-center gap-2 text-sm text-body">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => {
              touched.current.add('reorderExcluded');
              setSaved(false);
              setExcluded(e.target.checked);
            }}
            className="rounded border-line-strong"
          />
          Do not reorder (discontinued)
        </label>
        <PlanningInput
          label="Reason / note"
          value={fields.reorderNote}
          onChange={(v) => edit('reorderNote', v)}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <PlanningInput
            label="Case pack"
            value={fields.casePack}
            placeholder="1"
            onChange={(v) => edit('casePack', v)}
          />
          <PlanningInput
            label="Min order qty"
            value={fields.minOrderQty}
            onChange={(v) => edit('minOrderQty', v)}
          />
          <PlanningInput
            label="Lead time override (days)"
            value={fields.leadTimeDaysOverride}
            onChange={(v) => edit('leadTimeDaysOverride', v)}
            hint={
              suggested !== null ? (
                <span className="text-xs text-muted">
                  Observed p80: {integer(suggested)}d over {integer(observedN)}{' '}
                  {observedN === 1 ? 'order' : 'orders'}.{' '}
                  <button
                    type="button"
                    className="text-accent hover:underline"
                    onClick={() => edit('leadTimeDaysOverride', String(suggested))}
                  >
                    Use
                  </button>
                </span>
              ) : undefined
            }
          />
          <PlanningInput
            label="Unit cost"
            value={fields.unitCost}
            placeholder="0.00"
            onChange={(v) => edit('unitCost', v)}
          />
          <PlanningInput
            label={`Override quantity${currentPlan ? ` (${currentPlan.name})` : ''}`}
            value={fields.overrideQty}
            onChange={(v) => edit('overrideQty', v)}
            disabled={overrideLocked}
          />
          <PlanningInput
            label="Override note"
            value={fields.overrideNote}
            onChange={(v) => edit('overrideNote', v)}
            disabled={overrideLocked}
          />
        </div>
        {currentPlan ? (
          <p className="text-xs text-muted">
            The override applies to the current plan,{' '}
            <span className="font-medium text-strong">{currentPlan.name}</span>
            {currentPlan.status === 'sent' ? ' (sent — read-only).' : '.'}
          </p>
        ) : (
          <p className="text-xs text-muted">
            No plan is selected, so the quantity override cannot be set here.
          </p>
        )}
        {error && <Alert>{error}</Alert>}
        <div className="flex items-center justify-end gap-3 text-xs text-muted">
          {saved && <span>Saved.</span>}
          <Button type="submit" variant="secondary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save planning'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PlanningInput({
  label,
  value,
  placeholder,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted">{label}</span>
      <Input
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <span className="mt-1 block">{hint}</span> : null}
    </label>
  );
}

/** This SKU's purchase-plan block: what to buy and why. Editing is on the plan page. */
function PlanCard({ skuId }: { skuId: number }) {
  // hideDuplicates=false: this SKU may itself be a zero-stock duplicate, and its
  // own detail page must still show its plan row, not "No plan row yet".
  const plan = useQuery({
    queryKey: ['purchase-plan', 'sku', false],
    queryFn: () =>
      api.get<PurchasePlanResult<PlanRow>>('/api/purchase-plan?scope=sku&hideDuplicates=false'),
  });
  const row = plan.data?.rows.find((r) => r.id === skuId);

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-body">Purchase plan</h2>
        <Link to="/purchase-plan" className="text-xs text-brand-600 hover:underline">
          Open the plan
        </Link>
      </div>
      {plan.isPending ? (
        <p className="mt-2 text-sm text-muted">Loading…</p>
      ) : plan.isError ? (
        <div className="mt-2">
          <Alert>Could not load the plan: {plan.error.message}</Alert>
        </div>
      ) : !row ? (
        <p className="mt-2 text-sm text-muted">No plan row for this SKU yet.</p>
      ) : (
        <PlanRowFacts row={row} />
      )}
    </Card>
  );
}

function NotesCard({ sku, canEdit }: { sku: Sku; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(sku.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setNotes(sku.notes ?? '');
  }, [sku.id, sku.notes]);

  const save = useMutation({
    mutationFn: (value: string) =>
      api.patch<{ sku: Sku }>(`/api/skus/${sku.id}`, { notes: value || null }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['sku', String(sku.id)] });
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(caught instanceof ApiError ? caught.message : 'Could not save the notes.');
    },
  });

  const dirty = notes !== (sku.notes ?? '');

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Notes</h2>
      {canEdit ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(notes.trim());
          }}
          className="mt-2 space-y-2"
        >
          <Textarea
            rows={6}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setSaved(false);
            }}
            placeholder="Supplier, lead time, anything worth remembering about this SKU."
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex items-center justify-end gap-3 text-xs text-muted">
            {saved && !dirty && <span>Saved.</span>}
            <Button type="submit" variant="secondary" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save notes'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-2 text-sm whitespace-pre-wrap text-body">{sku.notes ?? '—'}</p>
      )}
    </Card>
  );
}

/**
 * The observed lead times for one SKU: every sent plan line with the shipment it
 * became, the dates, and the supplier / transit / total days. The receipt date and
 * the total lead time come from the inventory ledger; see docs/lead-times.md.
 */
function LeadTimesCard({ sellerSku }: { sellerSku: string }) {
  const query = useQuery({
    queryKey: ['sku-lead-times', sellerSku],
    queryFn: () => fetchSkuLeadTimes(sellerSku),
  });

  const adjacent = query.data ? hasAdjacentShipments(query.data.lines) : false;

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">Lead times</h2>
        {query.data && query.data.stats.n > 0 ? (
          <span className="text-xs text-muted">
            Median {integer(query.data.stats.medianTotal)}d · p80{' '}
            {integer(query.data.stats.p80Total)}d over {integer(query.data.stats.n)}{' '}
            {query.data.stats.n === 1 ? 'order' : 'orders'}
          </span>
        ) : null}
      </div>
      {adjacent && <AdjacentShipmentsNote />}
      {query.isPending ? (
        <div className="px-4 py-3">
          <Spinner label="Loading lead times" />
        </div>
      ) : query.isError ? (
        <div className="px-4 py-3">
          <Alert>{query.error.message}</Alert>
        </div>
      ) : query.data.lines.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted">
          No sent plan lines for this SKU yet. Once an order is marked sent and a shipment and
          receipt land, its measured lead time shows here.
        </p>
      ) : (
        <LeadTimeLinesTable lines={query.data.lines} />
      )}
    </Card>
  );
}
